import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { admin } from '../../lib/api/admin.ts'
import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { commands, machines } from '../../lib/api/resources.ts'
import type { WebChallenge, WebIpState } from '../../lib/api/types.ts'
import { openCommandIdOf } from '../../lib/commands/semantics.ts'
import { formatDayClock, formatDecimal } from '../../lib/format.ts'
import { agentHref } from '../../lib/logs/investigation.ts'
import { navigate } from '../../lib/router.tsx'
import { decodeExplanation, severityOfDetection } from '../../lib/sse/parse.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import {
  activityPresentation,
  CHALLENGE_CHOICES,
  challengeOutcome,
  choiceConsequence,
  choiceLabel,
  closedPresentation,
  formatRemaining,
  ipStatePresentation,
  requestLine,
  resolveChallengeParams,
  routeText,
  ruleText,
} from '../../lib/webdefense/semantics.ts'
import type { ChallengeChoice, WebOutcome } from '../../lib/webdefense/semantics.ts'
import { ExplanationList } from '../detections/AlertDetailsView.tsx'
import { RiskMeter, SeverityBadge } from '../detections/Severity.tsx'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { CopyValue } from '../primitives/CopyValue.tsx'
import { ConfirmDialog, Dialog } from '../primitives/Dialog.tsx'
import { Drawer } from '../primitives/Drawer.tsx'
import { RelativeTime } from '../primitives/RelativeTime.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { WebCommandTracking } from './WebCommandTracking.tsx'

/** Quanto falta para o challenge expirar. Relógio próprio, como o `RelativeTime`. */
export function ExpiresIn({ iso }: { iso: string }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const left = Date.parse(iso) - now
  // Menos de 1 h: quem decide precisa ver que o prazo está no fim.
  const tone = !Number.isFinite(left) || left <= 0 ? 'text-fg-muted' : left < 3_600_000 ? 'text-warn' : 'text-fg-secondary'
  return (
    <span className={`tnum ${tone}`} title={`${formatDayClock(iso)} UTC`}>
      {formatRemaining(left)}
    </span>
  )
}

export function IpStateBadge({ state }: { state: WebIpState | null }) {
  const p = ipStatePresentation(state)
  return (
    <Badge tone={p.tone} dashed={state === null}>
      {p.label}
    </Badge>
  )
}

type Step =
  | { kind: 'confirm'; choice: ChallengeChoice; key: string }
  | { kind: 'track'; choice: ChallengeChoice; id: string; initial: AgentCommand | null }
  | null

/**
 * A ficha de um challenge e as três respostas, cada uma um `resolve_challenge`
 * para a máquina do item. "Liberar o IP" sai no clique; as outras duas pedem
 * confirmação com a consequência escrita — a exceção vale para todos os IPs e o
 * bloqueio é definitivo.
 *
 * A fila não traz a evidência: ela vem do detalhe, lido ao abrir. O detalhe
 * também diz se o item saiu da fila depois da última leitura.
 */
export function ChallengeDrawer({
  challenge,
  unavailableReason,
  onClose,
  onResolved,
  onCommand,
}: {
  challenge: WebChallenge
  /** Do catálogo: por que esta conta não pode resolver. `null` = pode. */
  unavailableReason: string | null
  onClose: () => void
  /** O agente respondeu: a fila mudou, quem chama relê. */
  onResolved: () => void
  /** Um `resolve_challenge` foi aceito (ou já estava aberto): a linha o mostra "a caminho". */
  onCommand: (command: AgentCommand) => void
}) {
  const action = useAction()
  const [step, setStep] = useState<Step>(null)
  const [final, setFinal] = useState<WebOutcome | null>(null)

  const detailFetcher = useCallback(
    (signal: AbortSignal) => machines.webChallenge(challenge.endpoint_id, challenge.challenge_id, signal),
    [challenge.endpoint_id, challenge.challenge_id],
  )
  const detail = useResource(detailFetcher)
  const item = detail.data ?? challenge
  const closed = closedPresentation(item)

  const host = item.machine_name ?? item.endpoint_id
  const context = { ip: item.ip, rule: ruleText(item), route: routeText(item) }

  const track = (choice: ChallengeChoice, command: AgentCommand): void => {
    onCommand(command)
    setStep({ kind: 'track', choice, id: command.id, initial: command })
  }

  const send = (choice: ChallengeChoice, idempotencyKey: string): void =>
    action.run(async () => {
      try {
        const command = await admin.enqueueCommand(challenge.endpoint_id, {
          kind: 'resolve_challenge',
          params: resolveChallengeParams(challenge.challenge_id, choice),
          reason: null,
          idempotencyKey,
          acknowledgeNotDeliverable: false,
        })
        track(choice, command)
      } catch (cause) {
        // Já há uma decisão aberta para este challenge: acompanhe a que está em andamento.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            const open = await commands.get(id)
            if (open.params?.includes(challenge.challenge_id) === true) {
              track(choice, open)
              return
            }
          }
        }
        throw cause
      }
    })

  const choose = (choice: ChallengeChoice): void => {
    action.reset()
    // Uma chave por CLIQUE no botão; estável entre tentativas da mesma confirmação.
    const key = crypto.randomUUID()
    if (choiceConsequence(choice, context) === null) send(choice, key)
    else setStep({ kind: 'confirm', choice, key })
  }

  const reloadDetail = detail.reload
  const settled = useCallback(
    (outcome: WebOutcome) => {
      setFinal(outcome)
      onResolved()
      reloadDetail()
    },
    [onResolved, reloadDetail],
  )

  const blocked = unavailableReason !== null
  const done = final !== null || closed !== null

  return (
    <Drawer
      open
      onOpenChange={(open) => !open && onClose()}
      title={<span className="font-mono">{item.ip}</span>}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate font-mono">{host}</span>
          {item.state === 'pending' && (
            <>
              <span aria-hidden>·</span>
              <span>
                expira <ExpiresIn iso={item.expires_at} />
              </span>
            </>
          )}
        </span>
      }
      meta={
        <span className="flex items-center gap-2">
          <RiskMeter score={item.risk_score} compact />
          <SeverityBadge severity={severityOfDetection(item.severity)} />
        </span>
      }
      footer={
        <>
          {done ? (
            <span className="text-sm text-fg-secondary">
              {final === null
                ? 'Este challenge já saiu da fila.'
                : final.confirmed
                  ? 'Resolvido: o agente confirmou a decisão.'
                  : 'O agente não resolveu: o item saiu da fila como falha.'}
            </span>
          ) : (
            CHALLENGE_CHOICES.map((choice) => (
              <Button
                key={choice}
                size="sm"
                variant={choice === 'deny' ? 'danger' : choice === 'allow_ip' ? 'primary' : 'default'}
                disabled={blocked}
                busy={action.busy && step === null && choice === 'allow_ip'}
                title={unavailableReason ?? undefined}
                onClick={() => choose(choice)}
              >
                {choiceLabel(choice)}
              </Button>
            ))
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onClose()
              navigate(agentHref(item.endpoint_id, {}))
            }}
          >
            Logs da máquina
          </Button>
          {!done && blocked && <span className="basis-full text-micro text-fg-muted">{unavailableReason}</span>}
          {action.error !== null && step === null && (
            <p role="alert" className="basis-full text-sm leading-snug text-crit">
              {describeError(action.error)}
            </p>
          )}
        </>
      }
    >
      {closed !== null && final === null && (
        <div className="border-b border-line-subtle px-4 py-3">
          <Notice tone={closed.tone === 'crit' ? 'crit' : 'neutral'}>
            {closed.headline}
            {closed.detail !== null && <span className="mt-1 block font-mono text-micro break-words">{closed.detail}</span>}
          </Notice>
        </div>
      )}

      <ChallengeBody challenge={item} evidenceError={detail.error} onRetry={detail.reload} />

      {step?.kind === 'confirm' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setStep(null)}
          title={`${choiceLabel(step.choice)}?`}
          consequence={
            <span className="block space-y-1">
              <span className="block text-fg">{choiceConsequence(step.choice, context)}</span>
              <span className="block">
                IP <span className="font-mono text-fg">{item.ip}</span> em{' '}
                <span className="font-mono text-fg">{host}</span>.
              </span>
            </span>
          }
          confirmLabel={choiceLabel(step.choice)}
          destructive={step.choice === 'deny'}
          busy={action.busy}
          error={action.error !== null ? describeError(action.error) : null}
          onConfirm={() => send(step.choice, step.key)}
        />
      )}

      {step?.kind === 'track' && (
        <Dialog
          open
          onOpenChange={(open) => !open && (final !== null ? onClose() : setStep(null))}
          title={choiceLabel(step.choice)}
          description={
            <span>
              <span className="font-mono text-fg">{item.ip}</span> em{' '}
              <span className="font-medium text-fg">{host}</span>
            </span>
          }
          wide
        >
          <WebCommandTracking
            key={step.id}
            commandId={step.id}
            initial={step.initial}
            outcomeOf={(state) => challengeOutcome(step.choice, state, context)}
            toastLabel={item.ip}
            onSettled={settled}
            onClose={() => (final !== null ? onClose() : setStep(null))}
          />
        </Dialog>
      )}
    </Drawer>
  )
}

/**
 * O que o operador lê antes de decidir: a regra, a requisição, o risco, o que o
 * IP fez desde então e o porquê. A evidência só existe no detalhe: até ele
 * chegar, as linhas dela dizem que estão carregando.
 */
export function ChallengeBody({
  challenge,
  evidenceError,
  onRetry,
}: {
  challenge: WebChallenge
  evidenceError: unknown
  onRetry?: () => void
}) {
  const { evidence } = challenge
  const line = requestLine(challenge)
  const activity = activityPresentation(challenge.activity)
  const opened = Date.parse(challenge.opened_at)
  const gone = evidenceError instanceof AthenaError && evidenceError.code === 'web_challenge_not_found'
  const explanation = decodeExplanation(evidence?.explanation)

  const evidenceRow = (render: (e: NonNullable<WebChallenge['evidence']>) => ReactNode): ReactNode =>
    evidence !== null ? render(evidence) : <span className="text-fg-muted">{evidenceError === null ? 'carregando…' : '—'}</span>

  return (
    <div className="flex flex-col">
      {evidenceError !== null && (
        <div className="border-b border-line-subtle px-4 py-3">
          {gone ? (
            <Notice tone="neutral">Evidência indisponível para este challenge.</Notice>
          ) : (
            <ErrorState error={evidenceError} onRetry={onRetry} />
          )}
        </div>
      )}

      <Block title="Regra">
        <p className="text-sm text-fg">{challenge.rule_name ?? challenge.rule_id ?? 'Regra não informada'}</p>
        {challenge.rule_id !== null && (
          <CopyValue value={challenge.rule_id} display={challenge.rule_id} label="id da regra" className="text-micro" />
        )}
      </Block>

      <Block title="Requisição" aside="mascarada pelo agente">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <Row label="rota">
            {line === null ? '—' : <span className="font-mono break-all text-fg">{line}</span>}
          </Row>
          {challenge.host !== null && (
            <Row label="site">
              <span className="font-mono break-all text-fg">{challenge.host}</span>
            </Row>
          )}
          <Row label="query">{evidenceRow((e) => <Raw text={e.query} />)}</Row>
          <Row label="carga">{evidenceRow((e) => <Raw text={e.payload} />)}</Row>
          <Row label="user-agent">{evidenceRow((e) => <Raw text={e.user_agent} />)}</Row>
          {evidence?.request_id != null && (
            <Row label="request_id">
              <CopyValue value={evidence.request_id} label="request_id" className="text-micro" />
            </Row>
          )}
        </dl>
      </Block>

      <Block title="Risco" aside="correlação do IP e modelo">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <Row label="pontos">
            {evidenceRow((e) => (
              <>
                <span className="tnum font-mono text-fg">{formatDecimal(e.risk_points)}</span>
              </>
            ))}
          </Row>
          <Row label="modelo">
            {evidenceRow((e) =>
              e.contributions.length === 0 ? (
                <span className="text-fg-muted">sem contribuições</span>
              ) : (
                <ul className="flex flex-wrap gap-1">
                  {e.contributions.map((item) => (
                    <li key={item} className="border border-line px-1.5 py-px font-mono text-micro text-fg-secondary">
                      {item}
                    </li>
                  ))}
                </ul>
              ),
            )}
          </Row>
        </dl>
      </Block>

      <Block title="Desde que abriu" aside="aproximação">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          <Row label="abriu">
            <span className="tnum font-mono text-fg-secondary">{formatDayClock(challenge.opened_at)} UTC</span>
            <RelativeTime
              since={Number.isFinite(opened) ? opened : null}
              className="ml-2 text-micro text-fg-muted"
              intervalMs={30_000}
            />
          </Row>
          {challenge.state === 'pending' && (
            <Row label="expira">
              <ExpiresIn iso={challenge.expires_at} />
            </Row>
          )}
          <Row label="requisições">
            <span className="tnum font-mono text-fg">{activity.requests}</span>
            <span className="ml-2 text-micro text-fg-muted">
              {activity.hasSummary ? (activity.breakdown ?? 'todas atendidas') : 'nenhum resumo do IP ainda'}
            </span>
          </Row>
          <Row label="estado do IP">
            <IpStateBadge state={challenge.activity.state} />
            {challenge.activity.last_seen_at !== null && (
              <span className="ml-2 tnum text-micro text-fg-muted">
                último resumo {formatDayClock(challenge.activity.last_seen_at)} UTC
              </span>
            )}
          </Row>
        </dl>
      </Block>

      <Block
        title="Por que disparou"
        aside={explanation.length > 0 ? `${explanation.length} condições satisfeitas` : undefined}
      >
        {evidence === null ? (
          evidenceError === null ? (
            <LoadingRow label="Carregando a evidência…" />
          ) : (
            <p className="text-sm text-fg-muted">—</p>
          )
        ) : explanation.length === 0 ? (
          <p className="text-sm text-fg-muted">O agente não mandou a explicação da regra.</p>
        ) : (
          <ExplanationList explanation={explanation} />
        )}
      </Block>
    </div>
  )
}

function Block({ title, aside, children }: { title: string; aside?: string; children: ReactNode }) {
  return (
    <section className="border-b border-line-subtle px-4 py-3">
      <h3 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-micro font-medium uppercase tracking-label text-fg-secondary">
        {title}
        {aside !== undefined && <span className="font-normal normal-case tracking-[0] text-fg-muted">{aside}</span>}
      </h3>
      {children}
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="pt-px text-micro uppercase tracking-label text-fg-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  )
}

/** Texto do atacante, já mascarado: monoespaçado, quebrando em qualquer ponto, nunca interpretado. */
function Raw({ text }: { text: string | null }) {
  return text === null ? (
    <span className="text-fg-muted">—</span>
  ) : (
    <span className="block border-l-2 border-line-strong bg-inset px-1.5 py-0.5 font-mono text-micro break-all whitespace-pre-wrap text-fg">
      {text}
    </span>
  )
}
